/**
 * Which entity a post-form automation acts on, as selected from the form being edited.
 * 'firm' is a team-creating application; 'evaluatee' is the person seated in an evaluated position;
 * 'transmittal' is the person created or updated from a received NCARB transmittal.
 */
export type AutomationFormType = 'person' | 'team' | 'firm' | 'evaluatee' | 'transmittal';

import { AbstractControl, Validators } from '@angular/forms';
import { AttributeWrite } from '../attributes/attributeWrite';
import { PersonStatus } from 'src/app/people/models/personStatus';
export class BaseAutomationAction {
  id: string;
  automationActionType: AutomationActionType;
}
export class CredentialApplicationAutomationModel implements BaseAutomationModel {
  id: string;
  name: string;
  automationTrigger: AutomationTrigger;
  credentialApplicationAutomationActions: CredentialApplicationAutomationAction[];

  constructor(data?: CredentialApplicationAutomationModel) {
    if (!data) return;
    this.id = data.id;
    this.name = data.name;
    this.automationTrigger = new AutomationTrigger(data.automationTrigger);
    this.credentialApplicationAutomationActions = data.credentialApplicationAutomationActions.map(
      (a) => new CredentialApplicationAutomationAction(a)
    );
  }

  get triggeringActionTypeTitle(): string {
    switch (this.automationTrigger.triggeringActionType) {
      case TriggeringActionType.FormSubmitted:
        return 'Form Submitted';
      default:
        return '';
    }
  }
}

export class CredentialDefinitionAutomationModel {
  id: string;
  name: string;
  automationTrigger: AutomationTrigger;
  credentialDefinitionAutomationActions: CredentialDefinitionAutomationAction[];

  constructor(data?: CredentialDefinitionAutomationModel) {
    if (!data) return;
    this.id = data.id;
    this.name = data.name;
    this.automationTrigger = new AutomationTrigger(data.automationTrigger);
    this.credentialDefinitionAutomationActions = data.credentialDefinitionAutomationActions.map(
      (a) => new CredentialDefinitionAutomationAction(a)
    );
  }

  get triggeringActionTypeTitle(): string {
    switch (this.automationTrigger.triggeringActionType) {
      case TriggeringActionType.MaintenanceRule:
        return 'Maintenance Rule';
      default:
        return '';
    }
  }
}

export interface BaseAutomationModel {
  id: string;
  name: string;
  automationTrigger: AutomationTrigger;

  get triggeringActionTypeTitle(): string;
}

export class FormAutomationModel {
  id: string;
  name: string;
  automationTrigger: AutomationTrigger;
  credentialApplicationAutomationActions: CredentialApplicationAutomationAction[] = [];
  personTagAutomationActions: PersonTagAutomationAction[] = [];
  personCustomFieldAutomationActions: PersonCustomFieldAutomationAction[] = [];
  personStatusAutomationActions: PersonStatusAutomationAction[] = [];
  emailAutomationActions: EmailAutomationAction[] = [];
  sendLineupFormAutomationActions: SendLineupFormAutomationAction[] = [];
  conditionCount: number = 0;

  constructor(data?: FormAutomationModel) {
    if (!data) return;
    this.id = data.id;
    this.name = data.name;
    this.automationTrigger = new AutomationTrigger(data.automationTrigger);
    this.conditionCount = this.automationTrigger.oDataFilters?.length ?? 0;
    this.credentialApplicationAutomationActions = data.credentialApplicationAutomationActions.map(
      (a) => new CredentialApplicationAutomationAction(a)
    );
    this.personTagAutomationActions = data.personTagAutomationActions.map(
      (a) => new PersonTagAutomationAction(a)
    );
    this.personCustomFieldAutomationActions = data.personCustomFieldAutomationActions.map(
      (a) => new PersonCustomFieldAutomationAction(a)
    );
    this.personStatusAutomationActions = (data.personStatusAutomationActions ?? []).map(
      (a) => new PersonStatusAutomationAction(a)
    );
    this.emailAutomationActions = (data.emailAutomationActions ?? []).map(
      (a) => new EmailAutomationAction(a)
    );
    this.sendLineupFormAutomationActions = (data.sendLineupFormAutomationActions ?? []).map(
      (a) => new SendLineupFormAutomationAction(a)
    );
  }
}

export class AutomationTrigger {
  id: string;
  triggeringActionType: TriggeringActionType;
  entityId: string;
  oDataFilters: string[];
  oDataQuery: string;

  constructor(data?: AutomationTrigger) {
    if (!data) return;
    this.id = data.id;
    this.triggeringActionType = data.triggeringActionType;
    this.entityId = data.entityId;
    this.oDataFilters = data.oDataFilters ?? [];
    this.oDataQuery = data.oDataQuery;
  }
}

export class CredentialApplicationAutomationAction extends BaseAutomationAction {
  credentialApplicationDefinitionId: string;
  credentialApplicationScenarioId: string;
  applicationStatusLabelId: string | null;

  constructor(data?: CredentialApplicationAutomationAction) {
    super();
    if (!data) return;
    this.id = data.id;
    this.automationActionType = data.automationActionType;
    this.credentialApplicationDefinitionId = data.credentialApplicationDefinitionId;
    this.credentialApplicationScenarioId = data.credentialApplicationScenarioId;
    this.applicationStatusLabelId = data.applicationStatusLabelId ?? null;
  }

  isValid(): boolean {
    return (
      this.automationActionType === AutomationActionType.AddCredentialApplication &&
      this.credentialApplicationDefinitionId != null &&
      this.credentialApplicationDefinitionId.length > 0
    );
  }
}

export class CredentialDefinitionAutomationAction extends BaseAutomationAction {
  credentialDefinitionId: string;
  credentialCustomStatusDefinitionLabelId: string;

  constructor(data?: CredentialDefinitionAutomationAction) {
    super();
    if (!data) return;
    this.id = data.id;
    this.automationActionType = data.automationActionType;
    this.credentialDefinitionId = data.credentialDefinitionId;
    this.credentialCustomStatusDefinitionLabelId = data.credentialCustomStatusDefinitionLabelId;
  }
}

export class PersonTagAutomationAction extends BaseAutomationAction {
  tagIds: string[] = [];

  constructor(data?: PersonTagAutomationAction) {
    super();
    if (!data) return;

    this.automationActionType = data.automationActionType;
    this.tagIds = data.tagIds;
  }
  isValid(): boolean {
    return (
      (this.automationActionType === AutomationActionType.AddPersonTags ||
        this.automationActionType === AutomationActionType.RemovePersonTags) &&
      this.tagIds != null &&
      this.tagIds.length > 0
    );
  }
}

export class PersonCustomFieldAutomationAction extends BaseAutomationAction {
  attributeWriteModel: AttributeWrite;
  sourceAttributeDefinitionId?: string;
  constructor(data?: PersonCustomFieldAutomationAction) {
    super();
    if (!data) return;

    this.automationActionType = data.automationActionType;
    this.attributeWriteModel = new AttributeWrite(
      data.attributeWriteModel?.value,
      data.attributeWriteModel?.attributeDefinitionId,
      data.attributeWriteModel?.attributeDefinitionValueListItemId,
      data.attributeWriteModel?.values,
      data.attributeWriteModel?.entityType,
      data.attributeWriteModel?.uiType,
      data.attributeWriteModel?.otherValue
    );
    this.sourceAttributeDefinitionId = data.sourceAttributeDefinitionId;
  }

  isValid(): boolean {
    const hasSource = !!this.sourceAttributeDefinitionId;
    const hasStaticValue = this.attributeWriteModel?.hasValue();
    return (
      ((this.automationActionType === AutomationActionType.AppendPersonCustomFieldValue ||
        this.automationActionType === AutomationActionType.ReplacePersonCustomFieldValue) &&
        (hasSource || hasStaticValue)) ||
      this.automationActionType === AutomationActionType.ClearPersonCustomFieldValue
    );
  }
}

export class PersonStatusAutomationAction extends BaseAutomationAction {
  status: PersonStatus | null = null;

  constructor(data?: PersonStatusAutomationAction) {
    super();
    this.automationActionType = AutomationActionType.SetPersonStatus;
    if (!data) return;

    this.id = data.id;
    this.status = data.status;
  }

  isValid(): boolean {
    return (
      this.automationActionType === AutomationActionType.SetPersonStatus && this.status != null
    );
  }
}

/**
 * Reuses Angular's own validator on the raw value, so this cannot disagree with the
 * `Validators.email` on the action item's form control.
 */
function isEmailAddress(value: string | null | undefined): boolean {
  if (!value?.trim()) return false;
  return Validators.email({ value } as AbstractControl) === null;
}

export class EmailAutomationAction extends BaseAutomationAction {
  toEmail: string | null = null;
  toName: string | null = null;
  emailTemplateId: string | null = null;
  subject: string | null = null;
  content: string | null = null;
  fromName: string | null = null;
  replyTo: string | null = null;

  constructor(data?: EmailAutomationAction) {
    super();
    this.automationActionType = AutomationActionType.SendEmail;
    if (!data) return;

    this.id = data.id;
    this.toEmail = data.toEmail;
    this.toName = data.toName;
    this.emailTemplateId = data.emailTemplateId;
    this.subject = data.subject;
    this.content = data.content;
    this.fromName = data.fromName;
    this.replyTo = data.replyTo;
  }

  get usesTemplate(): boolean {
    return !!this.emailTemplateId;
  }

  isValid(): boolean {
    if (this.automationActionType !== AutomationActionType.SendEmail) return false;
    if (!isEmailAddress(this.toEmail)) return false;
    if (this.replyTo && !isEmailAddress(this.replyTo)) return false;

    return this.usesTemplate || (!!this.subject?.trim() && !!this.content?.trim());
  }
}

export class SendLineupFormAutomationAction extends BaseAutomationAction {
  formId: string | null = null;

  constructor(data?: SendLineupFormAutomationAction) {
    super();
    this.automationActionType = AutomationActionType.SendLineupForm;
    if (!data) return;

    this.id = data.id;
    this.formId = data.formId;
  }

  isValid(): boolean {
    return this.automationActionType === AutomationActionType.SendLineupForm && !!this.formId;
  }
}

export enum AutomationActionType {
  AddCredentialApplication = 'AddCredentialApplication',
  UpdateCredentialStatus = 'UpdateCredentialStatus',
  AddPersonTags = 'AddPersonTags',
  RemovePersonTags = 'RemovePersonTags',
  AppendPersonCustomFieldValue = 'AppendPersonCustomFieldValue',
  ReplacePersonCustomFieldValue = 'ReplacePersonCustomFieldValue',
  ClearPersonCustomFieldValue = 'ClearPersonCustomFieldValue',
  SetPersonStatus = 'SetPersonStatus',
  SendEmail = 'SendEmail',
  SendLineupForm = 'SendLineupForm'
}

export enum TriggeringActionType {
  FormSubmitted = 'FormSubmitted',
  MaintenanceRule = 'MaintenanceRule',
  TransmittalReceived = 'TransmittalReceived'
}
